import React, {useMemo, useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {CubeIcon, QrcodeIcon, SparklesIcon, WifiIcon} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {HardwareInsightFilters, InsightFilters} from "types/OperatorInsights";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import RoleSelect from "components/InsightControls/RoleSelect";
import {Role} from "types/Role";
import DailyInteractionsChart from 'components/Chart/DailyInteractionsChart';
import InteractionsChart from 'components/Chart/InteractionsChart';
import SiteSelect from "../../../components/InsightControls/SiteSelect";
import {Site} from "../../../types/Site";
import ExportButton from "../../../components/ExportButton";
import {useOperator} from "../../../context/OperatorContext";

// Helpers ----

const breadcrumbs = {"Performance": "/", "Interactions": "/insights/hardware"}

// Component ----

function HardwareInsights() {
  const {termFor} = useOperator();
  const [filters, setFilters] = useState<HardwareInsightFilters>({...defaultFilters, role: null});
  const [site, setSite] = useState<Site | null>(null);

  const sinceInTimezone = useMemo(() => filters.since?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.since]);
  const untilInTimezone = useMemo(() => filters.until?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.until]);

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'hardware', {...filters, since: sinceInTimezone, until: untilInTimezone}],
    ({signal}) => ngage.insights.hardware({...filters, since: sinceInTimezone, until: untilInTimezone}, {signal}),
    {
      keepPreviousData: true,
      enabled: !!sinceInTimezone && !!untilInTimezone,
    }
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectSite(site: Site | null) {
    setSite(site);
    setFilters({...filters, site: site?.id ?? null})
  }

  function selectRole(role: Role | null) {
    setFilters({...filters, role: role?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.hardwareExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-hardware-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page
      title="Interactions"
      subTitle='Understand more about how app users interact with you'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <SiteSelect id="select-site" name="site" label={termFor('Site')} onChange={selectSite}/>
          <RoleSelect id="select-role" name="role" label="Role" onChange={selectRole}/>
        </InsightControls>
      </Card>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={3}>
          {/*<StatBox*/}
          {/*  name="Hardware Items"*/}
          {/*  icon={CubeIcon}*/}
          {/*  stat={data?.summary.total_hardware_items.toLocaleString()}*/}
          {/*  change={data?.summary.new_hardware_items.toLocaleString()}*/}
          {/*  isLoading={isLoading}*/}
          {/*  tooltip={`The number of all hardware items added as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}*/}
          {/*/>*/}

          <StatBox
            name="Beacons"
            icon={WifiIcon}
            stat={data?.summary.total_beacons.toLocaleString()}
            change={data?.summary.new_beacons.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of beacons added as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="QR Codes"
            icon={QrcodeIcon}
            stat={data?.summary.total_qr_codes.toLocaleString()}
            change={data?.summary.new_qr_codes.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of QR codes added as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Total Interactions"
            icon={SparklesIcon}
            stat={data?.summary.total_hardware_interactions.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of hardware interactions recorded between ${sinceDateString} and ${untilDateString}.`}
          />
        </StatList>
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">

        <Card
          heading="Interactions"
          subheading={`Total interactions per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <InteractionsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading="Daily Interactions"
          subheading={`Interactions per weekday between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          <div className="h-96">
            {data && <DailyInteractionsChart data={data}/>}
          </div>
        </Card>

        {/*<Card*/}
        {/*  heading="Beacon Battery"*/}
        {/*  subheading={`Average beacon voltage per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}*/}
        {/*>*/}
        {/*  {data && <BeaconVoltageChart data={data} interval={filters.interval} compare={filters.compare}/>}*/}
        {/*</Card>*/}
      </div>
    </Page>
  );
}

export default HardwareInsights;